import * as React from 'react';
import { TweenMax } from 'gsap';
import css from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';
import { EFXBoost } from '@/components/UI/svg/river2';
import EFArrowDown from '@/components/UI/svg/Icons/EFArrowDown';
import EFVideo from '@/components/UI/EFVideo';
import EcoImg from 'eco-design/base/eco-img';

const App: React.FunctionComponent = () => {
    const { useEffect, useLang, useRouter, Link, useRef } = useGlobal();
    const { t } = useLang('productriver2');
    const { locale } = useRouter();
    const river2_I_videoRef = useRef();

    const [origin, setOrigin] = React.useState('');

    const isShowLink = ['us', 'ca', 'au', 'uk', 'za', 'sa', 'eu', 'mx'].includes(locale);

    const videoMap = {
        us: ['/video/river2/details/Small_us.webm', '/video/river2/details/Small_us.mp4'],
        au: ['/video/river2/details/Small_au.webm', '/video/river2/details/Small_au.mp4'],
        cn: ['/video/river2/details/Small_cn.webm', '/video/river2/details/Small_cn.mp4'],
        eu: ['/video/river2/details/Small_eu.webm', '/video/river2/details/Small_eu.mp4'],
        ph: ['/video/river2/details/Small_eu.webm', '/video/river2/details/Small_eu.mp4'],
        de: ['/video/river2/details/Small_eu.webm', '/video/river2/details/Small_eu.mp4'],
        fr: ['/video/river2/details/Small_eu.webm', '/video/river2/details/Small_eu.mp4'],
        it: ['/video/river2/details/Small_eu.webm', '/video/river2/details/Small_eu.mp4'],
        es: ['/video/river2/details/Small_eu.webm', '/video/river2/details/Small_eu.mp4'],
        cis: ['/video/river2/details/Small_eu.webm', '/video/river2/details/Small_eu.mp4'],
        jp: ['/video/river2/details/Small_jp.webm', '/video/river2/details/Small_jp.mp4'],
        uk: ['/video/river2/details/Small_uk.webm', '/video/river2/details/Small_uk.mp4'],
        za: ['/video/river2/details/Small_za.webm', '/video/river2/details/Small_za.mp4'],
        sa: ['/video/river2/details/Small_uk.webm', '/video/river2/details/Small_uk.mp4'],
        kr: ['/video/river2/details/Small_eu.webm', '/video/river2/details/Small_eu.mp4'],
    };

    let controller: any;
    useEffect(() => {
        controller = new ScrollMagic.Controller();
    }, []);

    useEffect(() => {
        const layoutH = typeof window !== 'undefined' ? window.innerHeight : 1042;
        new ScrollMagic.Scene({
            triggerElement: '.river2_detials_I',
            triggerHook: 1,
            offset: 200,
            duration: layoutH * 0.5,
        })
            .setTween(TweenMax.to('.river2_detials_I .logoBox', 0.5, { width: '131px', height: '36px' }))
            .addTo(controller);

        new ScrollMagic.Scene({
            triggerElement: '.river2_detials_I',
            triggerHook: 1,
            offset: layoutH * 1.4,
            duration: layoutH * 0.3,
        })
            .setTween(TweenMax.to('.river2_detials_I .logoBox', 0.5, { opacity: 0, y: -100 }))
            .addTo(controller);

        new ScrollMagic.Scene({
            triggerElement: '.river2_detials_I',
            triggerHook: 1,
            offset: layoutH * 1.4,
            duration: layoutH * 0.3,
        })
            .setTween(TweenMax.to('.river2_detials_I .textTop', 0.5, { opacity: 0, y: -100 }))
            .addTo(controller);

        new ScrollMagic.Scene({
            triggerElement: '.river2_detials_I',
            triggerHook: 1,
            offset: layoutH * 1.7,
            duration: layoutH * 0.3,
        })
            .setTween(TweenMax.to('.river2_detials_I .textBottom', 0.5, { opacity: 1, y: -150 }))
            .addTo(controller);

        new ScrollMagic.Scene({
            triggerElement: '.river2_detials_I .photoBox',
            triggerHook: 1,
        })
            .on('enter', () => {
                river2_I_videoRef?.current?.current?.currentTime === 0;
            })
            .addTo(controller);

        new ScrollMagic.Scene({
            triggerElement: '.river2_detials_I .photoBox',
            triggerHook: 1,
            offset: 200,
        })
            .on('enter', () => {
                river2_I_videoRef?.current?.play();
            })
            .addTo(controller);
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setOrigin(window.location.origin);
        }
    }, []);

    return (
        <section className={`${css.I_9} river2_detials_I`}>
            <div className="stickyBox">
                <div className="logoBox">
                    <EFXBoost className="xBoost-dom" width="100%" height="100%" />
                </div>
                <div className="textBox">
                    <div className="textTop">
                        <div className="title">{t('river2.details.sec9_title')}</div>
                        <div className="subTitle">{t('river2.details.sec9_xboost_up')}</div>
                    </div>
                    <div className="textBottom">
                        <div className="desc">
                            {t.rich('river2.details.sec9_desc', {
                                p1: (child: React.ReactNode) => <span className="title-p1">{child}</span>,
                                p2: (child: React.ReactNode) => <span className="title-p2">{child}</span>,
                            })}
                        </div>
                        {isShowLink && (
                            <Link href="/x-boost" className="stream-link" newWin>
                                <div className="link font-family-regular">
                                    <span className="linkText">{t('x_boost_link')}</span>
                                    <div className="icon">
                                        <EFArrowDown
                                            style={{
                                                fontSize: '17px',
                                                transform: 'rotate(-90deg)',
                                            }}
                                        />
                                    </div>
                                </div>
                            </Link>
                        )}
                    </div>
                </div>
                <div className="photoBox">
                    {locale === 'ph' ? (
                        <EcoImg className="fullImgs" pc="https://websiteoss.ecoflow.com/upload/image/1751271597539/a.jpg" />
                    ) : (
                        <EFVideo
                            ref={river2_I_videoRef}
                            sources={videoMap[locale as keyof typeof videoMap] || videoMap.us}
                            width="100%"
                            height="100%"
                            className="fullImgs"
                            loop={false}
                            autoPlay={false}
                        />
                    )}
                </div>
            </div>
        </section>
    );
};

export default App;
